import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Check, Eye, EyeOff, Copy, Download, RefreshCw, Shield, ShieldOff } from "lucide-react";
import { TwoFactorAuthService, TwoFactorAuthStorage } from "../../lib/twoFactorAuth";
import { toast } from "../../components/ui/use-toast";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const ADMIN_EMAIL = "admin@rawpa.com";

export const TwoFactorManagement = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [is2FASetup, setIs2FASetup] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [regeneratingCodes, setRegeneratingCodes] = useState(false);
  const [currentToken, setCurrentToken] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [isDisabling, setIsDisabling] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    load2FAStatus();
    return () => unsubscribe();
  }, [navigate, auth]);

  // Auto-refresh token every 30 seconds
  useEffect(() => {
    if (!is2FASetup) return;

    const interval = setInterval(async () => {
      const secret = TwoFactorAuthStorage.getSecret();
      if (secret) {
        const token = await TwoFactorAuthService.getCurrentToken(secret);
        setCurrentToken(token);
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [is2FASetup]);

  const load2FAStatus = async () => {
    const has2FA = TwoFactorAuthStorage.isSetup();
    setIs2FASetup(has2FA);
    
    if (has2FA) {
      const codes = TwoFactorAuthStorage.getBackupCodes();
      setBackupCodes(codes || []);
      
      // Get current token for display
      const secret = TwoFactorAuthStorage.getSecret();
      if (secret) {
        const token = await TwoFactorAuthService.getCurrentToken(secret);
        setCurrentToken(token);
      }
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setRegeneratingCodes(true);
    try {
      const newCodes = TwoFactorAuthService.generateBackupCodes();
      TwoFactorAuthStorage.setBackupCodes(newCodes);
      setBackupCodes(newCodes);
      setShowBackupCodes(true);
      
      toast({
        title: "Backup codes regenerated",
        description: "New backup codes have been generated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to regenerate backup codes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRegeneratingCodes(false);
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(TwoFactorAuthService.formatBackupCodes(backupCodes));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
      
      toast({
        title: "Backup codes copied",
        description: "Backup codes have been copied to clipboard.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy backup codes.",
        variant: "destructive",
      });
    }
  };

  const downloadBackupCodes = () => {
    const content = `RAWPA Admin - Backup Codes\n\n${TwoFactorAuthService.formatBackupCodes(backupCodes)}\n\nStore these codes securely. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rawpa-admin-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Backup codes downloaded",
      description: "Backup codes have been downloaded successfully.",
    });
  };

  const handleDisable2FA = async () => {
    if (!verificationToken) {
      toast({
        title: "Error",
        description: "Please enter a verification code.",
        variant: "destructive",
      });
      return;
    }

    setIsDisabling(true);
    try {
      const secret = TwoFactorAuthStorage.getSecret();
      if (!secret) {
        throw new Error("2FA not properly configured");
      }

      const isValid = await TwoFactorAuthService.verifyToken(verificationToken, secret);
      
      if (isValid) {
        TwoFactorAuthStorage.clear();
        setIs2FASetup(false);
        setBackupCodes([]);
        setCurrentToken("");
        setVerificationToken("");
        
        toast({
          title: "2FA disabled",
          description: "Two-factor authentication has been disabled successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: "Invalid verification code. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to disable 2FA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDisabling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-black">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/aAyuiOcvMq/dashboard')}
              className="text-sm"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pt-[140px] px-4 pb-8">
        <div className="max-w-[800px] mx-auto">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#daed6e] rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Two-Factor Authentication Management
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 text-center">
              Manage your two-factor authentication settings
            </p>
          </div>

          <div className="space-y-6">
            {/* 2FA Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {is2FASetup ? <Shield className="w-5 h-5 text-green-600" /> : <ShieldOff className="w-5 h-5 text-red-600" />}
                  2FA Status
                </CardTitle>
                <CardDescription>
                  Current status of two-factor authentication for your admin account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {is2FASetup ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {is2FASetup 
                        ? 'Your account is protected with Google Authenticator' 
                        : 'Enable 2FA to add an extra layer of security to your account'
                      }
                    </p>
                  </div>
                  <Badge variant={is2FASetup ? "default" : "secondary"}>
                    {is2FASetup ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                
                {!is2FASetup && (
                  <div className="mt-4">
                    <Button 
                      onClick={() => navigate('/aAyuiOcvMq/2fa-setup')}
                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    >
                      Setup 2FA
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {is2FASetup && (
              <>
                {/* Current Token Display */}
                <Card>
                  <CardHeader>
                    <CardTitle>Current Token</CardTitle>
                    <CardDescription>
                      Current 6-digit code from your Google Authenticator app
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-4">
                      <div className="bg-gray-100 px-4 py-2 rounded-lg">
                        <code className="text-lg font-mono tracking-widest">{currentToken}</code>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          const secret = TwoFactorAuthStorage.getSecret();
                          if (secret) {
                            setCurrentToken(TwoFactorAuthService.getCurrentToken(secret));
                          }
                        }}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      This token refreshes every 30 seconds. Click refresh to get the latest code.
                    </p>
                  </CardContent>
                </Card>

                {/* Backup Codes Management */}
                <Card>
                  <CardHeader>
                    <CardTitle>Backup Codes</CardTitle>
                    <CardDescription>
                      Manage your backup codes for account recovery
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{backupCodes.length} backup codes remaining</p>
                        <p className="text-sm text-gray-600">
                          Each code can only be used once for account recovery
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowBackupCodes(!showBackupCodes)}
                        >
                          {showBackupCodes ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          {showBackupCodes ? 'Hide' : 'Show'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyBackupCodes}
                        >
                          {copiedCodes ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copiedCodes ? 'Copied!' : 'Copy'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={downloadBackupCodes}
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRegenerateBackupCodes}
                          disabled={regeneratingCodes}
                        >
                          <RefreshCw className={`w-4 h-4 ${regeneratingCodes ? 'animate-spin' : ''}`} />
                          Regenerate
                        </Button>
                      </div>
                    </div>
                    
                    {showBackupCodes && (
                      <div className="bg-gray-100 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                          {backupCodes.map((code, index) => (
                            <div key={index} className="bg-white px-3 py-2 rounded border">
                              {code}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Disable 2FA */}
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700">Danger Zone</CardTitle>
                    <CardDescription>
                      Disable two-factor authentication (not recommended)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex space-x-2">
                      <Input
                        type="text"
                        value={verificationToken}
                        onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code to disable 2FA"
                        className="text-center text-lg font-mono tracking-widest"
                        maxLength={6}
                      />
                      <Button 
                        onClick={handleDisable2FA}
                        disabled={verificationToken.length !== 6 || isDisabling}
                        variant="destructive"
                      >
                        {isDisabling ? 'Disabling...' : 'Disable 2FA'}
                      </Button>
                    </div>
                    <p className="text-xs text-red-600">
                      Warning: Disabling 2FA will remove the extra security layer from your account.
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 